import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, Teleport as _Teleport, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2eeabdec"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "select-single" }
const _hoisted_2 = ["id", "aria-label", "aria-expanded", "aria-controls"]
const _hoisted_3 = { class: "box-label" }
const _hoisted_4 = ["id", "aria-labelledby", "aria-activedescendant"]
const _hoisted_5 = ["id", "aria-selected", "data-selected", "data-highlighted", "onClick", "onMouseenterCapture"]
const _hoisted_6 = { class: "option-label truncate" }
const _hoisted_7 = {
  key: 1,
  class: "option-count"
}

import { nextTick, ref, watch } from "vue";
import { uniqueId } from "lodash";
import { Option, Options } from "./AppSelectSingle";
import { wrap } from "@/util/math";
import { useFloating } from "@/util/composables";

interface Props {
  /** two-way bound selected item state */
  modelValue?: Option;
  /** name of the field */
  name: string;
  /** list of options to show */
  options: Options;
}

interface Emits {
  /** two-way bound selected item state */
  (event: "update:modelValue", value: Option): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppSelectSingle',
  props: {
    modelValue: null,
    name: null,
    options: null
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }: { emit: ({
  /** two-way bound selected item state */
  (event: "update:modelValue", value: Option): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /** two-way bound selected item state */
  modelValue?: Option;
  /** name of the field */
  name: string;
  /** list of options to show */
  options: Options;
}





/** unique id for instance of component */
const id = ref(uniqueId());
/** whether dropdown is open */
const expanded = ref(false);
/** index of option that is selected */
const selected = ref(getSelected());
/** index of option that is highlighted */
const highlighted = ref(0);

/** target element */
const target = ref();
/** dropdown element */
const dropdown = ref();
/** get dropdown position */
const { calculate, style } = useFloating(target, dropdown);
/** recompute position after opened */
watch(expanded, async () => {
  await nextTick();
  if (expanded.value) calculate();
});

/** open dropdown */
function open() {
  expanded.value = true;
  /** auto highlight selected option */
  highlighted.value = selected.value;
}

/** close dropdown */
function close() {
  expanded.value = false;
}

/** when button clicked */
function onClick() {
  /** toggle dropdown */
  expanded.value ? close() : open();
  /** https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus */
  (document.querySelector(`#select-${id.value}`) as HTMLElement)?.focus();
}

/** when button blurred */
function onBlur() {
  close();
}

/** when user presses key on button */
function onKeydown(event: KeyboardEvent) {
  /** arrow/home/end keys */
  if (["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
    /** prevent page scroll */
    event.preventDefault();

    /**
     * if dropdown open, control highlighted option. if dropdown closed, control
     * selected option.
     */
    let index = expanded.value ? highlighted.value : selected.value;

    /** move value up/down */
    if (event.key === "ArrowUp") index--;
    if (event.key === "ArrowDown") index++;
    if (event.key === "Home") index = 0;
    if (event.key === "End") index = props.options.length - 1;

    /** update value, wrapping beyond 0 or options length */
    index = wrap(index, 0, props.options.length - 1);
    if (expanded.value) highlighted.value = index;
    else selected.value = index;
  }

  /** enter key to select highlighted option */
  if (expanded.value && (event.key === "Enter" || event.key === " ")) {
    /** prevent browser re-clicking open button */
    event.preventDefault();
    selected.value = highlighted.value;
  }

  /** esc key to close dropdown */
  if (expanded.value && event.key === "Escape") close();
}

/** get selected option index from model */
function getSelected() {
  return props.options.findIndex(
    (option) => option?.id === props.modelValue?.id
  );
}

/** when model changes */
watch(
  () => props.modelValue,
  () =>
    /** update selected index */
    (selected.value = getSelected())
);

/** when selected index changes */
watch(selected, () => {
  /** emit updated model */
  emit("update:modelValue", props.options[selected.value]);
  close();
});

/** when highlighted index changes */
watch(highlighted, () =>
  /** scroll to highlighted in dropdown */
  document
    .querySelector(`#option-${id.value}-${highlighted.value}`)
    ?.scrollIntoView({ block: "nearest" })
);

/** auto-select first option as fallback */
watch(
  () => props.options,
  () => {
    if (selected.value === -1 && props.options.length) selected.value = 0;
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      id: `select-${id.value}`,
      ref_key: "target",
      ref: target,
      class: "box",
      "aria-label": __props.name,
      "aria-expanded": expanded.value,
      "aria-controls": `list-${id.value}`,
      "aria-haspopup": "listbox",
      tabindex: "0",
      onClick: onClick,
      onKeydown: onKeydown,
      onBlur: onBlur
    }, [
      (__props.modelValue?.icon)
        ? (_openBlock(), _createBlock(_component_AppIcon, {
            key: 0,
            icon: __props.modelValue?.icon
          }, null, 8, ["icon"]))
        : _createCommentVNode("", true),
      _createElementVNode("span", _hoisted_3, _toDisplayString(__props.modelValue?.name || __props.modelValue?.id), 1),
      _createVNode(_component_AppIcon, {
        icon: expanded.value ? 'angle-up' : 'angle-down'
      }, null, 8, ["icon"])
    ], 40, _hoisted_2),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (expanded.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            id: `list-${id.value}`,
            ref_key: "dropdown",
            ref: dropdown,
            class: "list",
            role: "listbox",
            "aria-labelledby": `select-${id.value}`,
            "aria-activedescendant": `option-${id.value}-${highlighted.value}`,
            tabindex: "0",
            style: _normalizeStyle(_unref(style))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option, index) => {
              return _withDirectives((_openBlock(), _createElementBlock("div", {
                id: `option-${id.value}-${index}`,
                key: index,
                class: "option",
                role: "option",
                "aria-selected": selected.value === index,
                "data-selected": selected.value === index,
                "data-highlighted": index === highlighted.value,
                tabindex: "0",
                onClick: ($event: any) => (selected.value = index),
                onMouseenterCapture: ($event: any) => (highlighted.value = index),
                onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
                onFocusin: _cache[1] || (_cache[1] = () => null),
                onKeydown: _cache[2] || (_cache[2] = () => null)
              }, [
                (option.icon)
                  ? (_openBlock(), _createBlock(_component_AppIcon, {
                      key: 0,
                      icon: option.icon,
                      class: "option-icon"
                    }, null, 8, ["icon"]))
                  : _createCommentVNode("", true),
                _createElementVNode("span", _hoisted_6, _toDisplayString(option.name || option.id), 1),
                (option.count)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(option.count), 1))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_5)), [
                [_directive_tooltip, option.tooltip]
              ])
            }), 128))
          ], 12, _hoisted_4))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})